<script>
/**
 * Parâmetros do sistema — configurações guardadas no BANCO (tabela `parametro`),
 * não no .env. Cada instalação tem as suas (chaves de API, limites, etc.) e o
 * admin edita aqui, sem acesso ao servidor e sem restart.
 *
 * A tela é genérica: renderiza o que o backend devolve em GET /parametro,
 * agrupado e tipado. Para criar um parâmetro novo basta declará-lo em
 * `backend/src/app/services/ParametroCatalogo.js`.
 *
 * Valores marcados como secretos nunca chegam ao frontend — o campo mostra só
 * se está preenchido; deixar em branco mantém o valor atual.
 */
import axios from 'axios';
import { ehAdmin } from '@/helpers/permissao';
import Layout from '@/layouts/main.vue';
import PageHeader from '@/components/page-header';

export default {
  name: 'Parametros',
  components: { Layout, PageHeader },
  data() {
    return {
      carregando: true,
      salvando: false,
      erro: null,
      sucesso: null,

      grupos: [],
      sincronizando: false,

      // Form de criação de parâmetro avulso (fora do catálogo)
      criando: false,
      salvandoNovo: false,
      novo: {
        chave: '', rotulo: '', grupo: '', tipo: 'texto', descricao: '', valor: '', secreto: false,
      },

      // chave → valor no formulário (secretos começam vazios)
      form: {},
      // chave → valor carregado, para detectar o que mudou
      original: {},
      // chaves secretas que o usuário pediu para limpar
      limpar: [],
      // chaves com valor secreto visível durante a digitação
      revelados: [],
    };
  },

  computed: {
    alterados() {
      const out = {};
      this.grupos.forEach((g) => g.itens.forEach((item) => {
        const atual = this.form[item.chave];
        if (item.secreto) {
          if (this.limpar.includes(item.chave)) out[item.chave] = null;
          else if (String(atual || '').trim() !== '') out[item.chave] = atual;
          return;
        }
        if (String(atual ?? '') !== String(this.original[item.chave] ?? '')) {
          out[item.chave] = atual;
        }
      }));
      return out;
    },
    temAlteracao() { return Object.keys(this.alterados).length > 0; },
  },

  async created() {
    const user = JSON.parse(localStorage.getItem('userdata') || '{}');
    if (!ehAdmin(user)) {
      this.$router.replace('/');
      return;
    }
    await this.carregar();
  },

  methods: {
    async carregar() {
      this.carregando = true;
      this.erro = null;
      try {
        const { data } = await axios.get('/parametro');
        this.aplicar(data.grupos || []);
      } catch (error) {
        this.erro = `Falha ao carregar parâmetros: ${this.mensagemErro(error)}`;
      } finally {
        this.carregando = false;
      }
    },

    /**
     * Junta a mensagem amigável do backend com o `details` (causa real, ex.:
     * "Table 'x.parametro' doesn't exist"). Sem isso, um 500 vira uma frase
     * genérica e o diagnóstico exige olhar o log do servidor.
     */
    mensagemErro(error) {
      const data = error.response?.data || {};
      const base = data.error || error.message;
      return data.details && data.details !== base ? `${base} — ${data.details}` : base;
    },

    aplicar(grupos) {
      this.grupos = grupos;
      const form = {};
      const original = {};
      grupos.forEach((g) => g.itens.forEach((item) => {
        // Secreto nunca vem do backend: campo começa vazio = "não mexi".
        const valor = item.secreto ? '' : (item.valor ?? '');
        form[item.chave] = item.tipo === 'booleano' ? String(valor) === 'true' : valor;
        original[item.chave] = form[item.chave];
      }));
      this.form = form;
      this.original = original;
      this.limpar = [];
      this.revelados = [];
    },

    async salvar() {
      if (!this.temAlteracao || this.salvando) return;
      this.salvando = true;
      this.erro = null;
      this.sucesso = null;
      try {
        const valores = {};
        Object.entries(this.alterados).forEach(([chave, v]) => {
          valores[chave] = typeof v === 'boolean' ? String(v) : v;
        });
        const { data } = await axios.put('/parametro/salvar', { valores });
        this.aplicar(data.grupos || []);
        const qtd = (data.salvos || []).length;
        this.sucesso = `${qtd} parâmetro(s) salvo(s). As alterações valem imediatamente.`;
        if (data.invalidos && data.invalidos.length) {
          this.erro = `Não salvos: ${data.invalidos.map((i) => `${i.chave} (${i.motivo})`).join(', ')}.`;
        }
      } catch (error) {
        this.erro = `Falha ao salvar: ${this.mensagemErro(error)}`;
      } finally {
        this.salvando = false;
      }
    },

    /**
     * Reexecuta a carga do catálogo declarado em código. Resolve o caso em que
     * o boot do servidor falhou nessa etapa e a tela ficou sem nenhum item.
     */
    async sincronizar() {
      if (this.sincronizando) return;
      this.sincronizando = true;
      this.erro = null;
      this.sucesso = null;
      try {
        const { data } = await axios.post('/parametro/sincronizar');
        this.aplicar(data.grupos || []);
        this.sucesso = data.criados
          ? `${data.criados} parâmetro(s) do catálogo registrado(s).`
          : 'Catálogo já estava sincronizado.';
      } catch (error) {
        this.erro = `Falha ao sincronizar: ${this.mensagemErro(error)}`;
      } finally {
        this.sincronizando = false;
      }
    },

    async criarParametro() {
      if (this.salvandoNovo) return;
      this.salvandoNovo = true;
      this.erro = null;
      this.sucesso = null;
      try {
        const { data } = await axios.post('/parametro/criar', {
          ...this.novo,
          secreto: this.novo.secreto || this.novo.tipo === 'senha',
        });
        this.aplicar(data.grupos || []);
        this.sucesso = `Parâmetro "${this.novo.chave}" criado.`;
        this.novo = { chave: '', rotulo: '', grupo: '', tipo: 'texto', descricao: '', valor: '', secreto: false };
        this.criando = false;
      } catch (error) {
        this.erro = `Falha ao criar: ${this.mensagemErro(error)}`;
      } finally {
        this.salvandoNovo = false;
      }
    },

    async removerParametro(item) {
      if (!window.confirm(`Remover o parâmetro "${item.chave}"? O valor configurado será perdido.`)) return;
      this.erro = null;
      try {
        const { data } = await axios.delete(`/parametro/remover/${encodeURIComponent(item.chave)}`);
        this.aplicar(data.grupos || []);
        this.sucesso = `Parâmetro "${item.chave}" removido.`;
      } catch (error) {
        this.erro = `Falha ao remover: ${this.mensagemErro(error)}`;
      }
    },

    descartar() {
      this.form = { ...this.original };
      this.limpar = [];
      this.sucesso = null;
    },

    alternarLimpar(chave) {
      const i = this.limpar.indexOf(chave);
      if (i >= 0) this.limpar.splice(i, 1);
      else {
        this.limpar.push(chave);
        this.form[chave] = '';
      }
    },

    alternarRevelar(chave) {
      const i = this.revelados.indexOf(chave);
      if (i >= 0) this.revelados.splice(i, 1);
      else this.revelados.push(chave);
    },

    origemBadge(origem) {
      const map = {
        banco: { classe: 'bg-success-subtle text-success', texto: 'banco' },
        env: { classe: 'bg-warning-subtle text-warning', texto: '.env' },
        padrao: { classe: 'bg-secondary-subtle text-secondary', texto: 'padrão' },
      };
      return map[origem] || { classe: 'bg-secondary-subtle text-secondary', texto: 'não definido' };
    },

    placeholderSecreto(item) {
      if (this.limpar.includes(item.chave)) return 'Será apagado ao salvar';
      return item.preenchido ? '•••••••••• (configurado — deixe em branco para manter)' : 'Não configurado';
    },
  },
};
</script>

<template>
  <Layout>
    <PageHeader title="Parâmetros" pageTitle="Customização" />

    <BCard no-body>
      <div class="card-header d-flex justify-content-between align-items-start flex-wrap gap-2">
        <div>
          <h5 class="m-0">Configurações do sistema</h5>
          <small class="text-muted">
            Guardadas no banco desta instalação — cada projeto tem as suas. Alterações valem na hora,
            sem reiniciar o servidor. Chaves e senhas nunca são exibidas depois de salvas.
          </small>
        </div>
        <div class="d-flex gap-2">
          <button type="button" class="btn btn-sm btn-outline-secondary" :disabled="sincronizando" @click="sincronizar">
            <i v-if="sincronizando" class="ri-loader-4-line spin me-1"></i>
            <i v-else class="ri-refresh-line me-1"></i>Sincronizar catálogo
          </button>
          <button type="button" class="btn btn-sm btn-outline-primary" @click="criando = !criando">
            <i class="ri-add-line me-1"></i>Novo parâmetro
          </button>
        </div>
      </div>

      <div class="card-body">
        <div v-if="erro" class="alert alert-danger">
          <i class="ri-error-warning-line me-1"></i>{{ erro }}
          <button class="btn-close float-end" @click="erro = null"></button>
        </div>
        <div v-if="sucesso" class="alert alert-success">
          <i class="ri-checkbox-circle-line me-1"></i>{{ sucesso }}
          <button class="btn-close float-end" @click="sucesso = null"></button>
        </div>

        <!-- Criação de parâmetro avulso -->
        <div v-if="criando" class="grupo-card mb-3">
          <h6 class="grupo-titulo">Novo parâmetro</h6>
          <div class="row g-2">
            <div class="col-md-4">
              <label class="form-label">Chave <span class="text-danger">*</span></label>
              <input v-model="novo.chave" class="form-control" placeholder="ex: integracao.token" />
              <small class="text-muted">Minúsculas, números e "_", separados por ponto.</small>
            </div>
            <div class="col-md-4">
              <label class="form-label">Rótulo <span class="text-danger">*</span></label>
              <input v-model="novo.rotulo" class="form-control" placeholder="ex: Token da integração" />
            </div>
            <div class="col-md-4">
              <label class="form-label">Grupo</label>
              <input v-model="novo.grupo" class="form-control" placeholder="ex: Integrações (padrão: Geral)" />
            </div>
            <div class="col-md-3">
              <label class="form-label">Tipo</label>
              <select v-model="novo.tipo" class="form-select">
                <option value="texto">Texto</option>
                <option value="numero">Número</option>
                <option value="booleano">Sim / Não</option>
                <option value="senha">Senha / chave (oculto)</option>
                <option value="json">JSON</option>
              </select>
            </div>
            <div class="col-md-9">
              <label class="form-label">Descrição</label>
              <input v-model="novo.descricao" class="form-control" placeholder="Explique para que serve" />
            </div>
            <div class="col-md-6">
              <label class="form-label">Valor</label>
              <input
                v-model="novo.valor"
                :type="novo.tipo === 'senha' ? 'password' : 'text'"
                class="form-control"
                autocomplete="new-password"
                placeholder="Pode preencher depois"
              />
            </div>
          </div>
          <div class="d-flex justify-content-end gap-2 mt-3">
            <button type="button" class="btn btn-light" @click="criando = false">Cancelar</button>
            <button
              type="button"
              class="btn btn-primary"
              :disabled="salvandoNovo || !novo.chave || !novo.rotulo"
              @click="criarParametro"
            >
              <i v-if="salvandoNovo" class="ri-loader-4-line spin me-1"></i>
              <i v-else class="ri-add-line me-1"></i>Criar parâmetro
            </button>
          </div>
        </div>

        <div v-if="carregando" class="text-center text-muted py-4">
          <i class="ri-loader-4-line spin"></i> Carregando...
        </div>

        <div v-else-if="!grupos.length" class="text-center text-muted py-4">
          <i class="ri-settings-3-line" style="font-size: 1.5rem;"></i>
          <div class="mt-1">Nenhum parâmetro registrado.</div>
          <small class="d-block mt-1">
            Os parâmetros declarados em código entram por <strong>Sincronizar catálogo</strong>.
            Para uma configuração que não existe no código, use <strong>Novo parâmetro</strong>.
          </small>
          <button type="button" class="btn btn-sm btn-primary mt-2" :disabled="sincronizando" @click="sincronizar">
            <i v-if="sincronizando" class="ri-loader-4-line spin me-1"></i>
            <i v-else class="ri-refresh-line me-1"></i>Sincronizar catálogo agora
          </button>
        </div>

        <template v-else>
          <div v-for="grupo in grupos" :key="grupo.nome" class="grupo-card mb-3">
            <h6 class="grupo-titulo">{{ grupo.nome }}</h6>

            <div v-for="item in grupo.itens" :key="item.chave" class="param-linha">
              <div class="param-info">
                <label class="form-label mb-0" :for="`p-${item.chave}`">{{ item.rotulo }}</label>
                <span class="badge ms-2" :class="origemBadge(item.origem).classe">
                  {{ origemBadge(item.origem).texto }}
                </span>
                <span v-if="!item.no_catalogo" class="badge bg-info-subtle text-info ms-1" title="Criado pela tela, não declarado em código">
                  avulso
                </span>
                <button
                  v-if="!item.no_catalogo"
                  type="button"
                  class="btn btn-sm btn-link text-danger p-0 ms-2 align-baseline"
                  title="Remover este parâmetro"
                  @click="removerParametro(item)"
                >
                  <i class="ri-delete-bin-line"></i>
                </button>
                <code class="param-chave d-block">{{ item.chave }}</code>
                <small v-if="item.descricao" class="text-muted d-block">{{ item.descricao }}</small>
                <small v-if="item.origem === 'env'" class="text-warning d-block">
                  <i class="ri-information-line me-1"></i>
                  Valor vindo da variável de ambiente <code>{{ item.env }}</code>. Ao salvar aqui, o banco passa a mandar.
                </small>
              </div>

              <div class="param-campo">
                <!-- Booleano -->
                <div v-if="item.tipo === 'booleano'" class="form-check form-switch">
                  <input
                    :id="`p-${item.chave}`"
                    v-model="form[item.chave]"
                    class="form-check-input"
                    type="checkbox"
                  />
                  <label class="form-check-label small text-muted" :for="`p-${item.chave}`">
                    {{ form[item.chave] ? 'Ativado' : 'Desativado' }}
                  </label>
                </div>

                <!-- Secreto (senha / chave de API) -->
                <div v-else-if="item.secreto" class="input-group">
                  <input
                    :id="`p-${item.chave}`"
                    v-model="form[item.chave]"
                    :type="revelados.includes(item.chave) ? 'text' : 'password'"
                    class="form-control"
                    autocomplete="new-password"
                    :placeholder="placeholderSecreto(item)"
                    :disabled="limpar.includes(item.chave)"
                  />
                  <button
                    type="button"
                    class="btn btn-outline-secondary"
                    :title="revelados.includes(item.chave) ? 'Ocultar' : 'Mostrar o que estou digitando'"
                    @click="alternarRevelar(item.chave)"
                  >
                    <i :class="revelados.includes(item.chave) ? 'ri-eye-off-line' : 'ri-eye-line'"></i>
                  </button>
                  <button
                    v-if="item.preenchido"
                    type="button"
                    class="btn"
                    :class="limpar.includes(item.chave) ? 'btn-danger' : 'btn-outline-danger'"
                    :title="limpar.includes(item.chave) ? 'Cancelar remoção' : 'Apagar o valor salvo'"
                    @click="alternarLimpar(item.chave)"
                  >
                    <i class="ri-delete-bin-line"></i>
                  </button>
                </div>

                <!-- JSON -->
                <textarea
                  v-else-if="item.tipo === 'json'"
                  :id="`p-${item.chave}`"
                  v-model="form[item.chave]"
                  class="form-control font-monospace"
                  rows="4"
                ></textarea>

                <!-- Número / texto -->
                <input
                  v-else
                  :id="`p-${item.chave}`"
                  v-model="form[item.chave]"
                  :type="item.tipo === 'numero' ? 'number' : 'text'"
                  class="form-control"
                  :placeholder="item.padrao ? `Padrão: ${item.padrao}` : ''"
                />
              </div>
            </div>
          </div>

          <div class="d-flex justify-content-end gap-2">
            <button type="button" class="btn btn-light" :disabled="!temAlteracao || salvando" @click="descartar">
              Descartar
            </button>
            <button type="button" class="btn btn-success" :disabled="!temAlteracao || salvando" @click="salvar">
              <i v-if="salvando" class="ri-loader-4-line spin me-1"></i>
              <i v-else class="ri-save-line me-1"></i>
              {{ salvando ? 'Salvando...' : 'Salvar alterações' }}
            </button>
          </div>
        </template>
      </div>
    </BCard>
  </Layout>
</template>

<style scoped>
.spin { display: inline-block; animation: spin 1s linear infinite; }
@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }

.grupo-card {
  border: 1px solid var(--vz-border-color);
  border-radius: 8px;
  padding: 1rem 1.25rem;
  background: var(--vz-card-bg, var(--vz-body-bg));
}
[data-bs-theme="dark"] .grupo-card {
  background: var(--vz-dark, #2a2f3a);
}

.grupo-titulo {
  margin: 0 0 0.75rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid var(--vz-border-color);
  color: var(--vz-body-color);
}

.param-linha {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(240px, 380px);
  gap: 1rem;
  align-items: start;
  padding: 0.75rem 0;
  border-bottom: 1px dashed var(--vz-border-color);
}
.param-linha:last-child { border-bottom: 0; padding-bottom: 0; }

.param-chave {
  font-size: 0.75rem;
  opacity: 0.65;
  margin-top: 0.15rem;
}

@media (max-width: 767.98px) {
  .param-linha { grid-template-columns: 1fr; gap: 0.5rem; }
}
</style>
